import clsx from "clsx";
import { type FC } from "react";

import { GroupConfigLabel } from "../GroupConfigLabel";
import { usePageGroupOperation } from "../use-page-group-operation";

import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon } from "mds/icons";
import { storeApi } from "store/index";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { GROUP_INTERACTION_MODES, GroupInteractionModeType } from "utils/page-utils";

interface InteractionModeConfigProps {
  pageId: string;
  fullWidth?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  title: string;
}

type GroupInteractionMode =
  | InteractionMode.ASSIGN_STUDENTS
  | InteractionMode.SELF_ASSIGN
  | InteractionMode.COPY_GROUPS;

export const InteractionModeConfig: FC<InteractionModeConfigProps> = ({
  pageId,
  fullWidth = false,
  isDisabled,
  isReadOnly,
  title,
}) => {
  const { isPending, runPageGroupOperation } = usePageGroupOperation(pageId);
  const controlsDisabled = isDisabled || isPending;
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);
  const interactionMode = page?.interaction_mode as GroupInteractionMode | null;

  if (!interactionMode) return null;

  const onSelect = (currentInteractionMode: GroupInteractionMode) => {
    if (controlsDisabled || isReadOnly || currentInteractionMode === interactionMode) return;
    runPageGroupOperation(async () => {
      await storeApi.pages.partial_update({ id: pageId, interaction_mode: currentInteractionMode });
    }).catch(() => {}); // Store requests already report errors.
  };

  return (
    <Tooltip content={controlsDisabled || isReadOnly ? title : ""}>
      <Listbox
        disabled={controlsDisabled || isReadOnly}
        eventName="Group selection method updated"
        items={GROUP_INTERACTION_MODES.map((choice) => ({
          id: choice,
          title: t(`group_configuration_card.choice.${choice as GroupInteractionModeType}`),
        }))}
        selectedValue={interactionMode}
        triggerButton={
          <SquareButton
            aria-label={t(`group_configuration_card.group_type.${interactionMode}`)}
            className={clsx(fullWidth && "w-full justify-between")}
            kind="ghost"
            size="s"
          >
            <GroupConfigLabel
              full={t(`group_configuration_card.group_type.${interactionMode}`)}
              short={t(`group_configuration_card.group_type_short.${interactionMode}`)}
            />
            {!isReadOnly && <ChevronDownIcon className={clsx(fullWidth && "ml-auto")} />}
          </SquareButton>
        }
        onSelectionChange={({ id }) => onSelect(id as GroupInteractionMode)}
      />
    </Tooltip>
  );
};
